import { Allotment } from 'allotment'
import { useEffect, useMemo, useState } from 'react'

import { Attribute, AttributeTypeEnum, fetchAttributes, selectOrderAttributes, selectVehicleAttributes } from '@/entities/attributes'
import { Constraint, fetchConstraints, saveConstraint, unlinkConstraint } from '@/entities/constraints'
import { constraintsSelectors } from '@/entities/constraints/constraintsSelectors'
import { Button, Icon, IconButton, ToggleButton } from '@/shared/components'
import { DynamicForm } from '@/shared/components/DynamicForm/DynamicForm'
import { CustomGridToolbar } from '@/shared/components/GridTable/CustomGridToolbar'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faAdd, faChain, faChainBroken, faClose, faEdit, faLinkSimple, faLinkSimpleSlash } from '@fortawesome/pro-regular-svg-icons'
import { Divider, Grid, styled } from '@mui/material'
import { GridColDef, GridInputRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid-premium'
import { EntityId } from '@reduxjs/toolkit'

import { getConstraintFormDefinition } from '../libs/getConstraintFormDefinition'
import {
  StyledAddButton, StyledButtonsContainer, StyledContainer, StyledDescription, StyledHeader, StyledLeftPane, StyledRightPane,
} from '../StyledOutletPage'
import { LinkConstraintsDialog } from './LinkConstraintsDialog'

export type ConstraintsPageProps = {
  //
}

// Create a composite row type that can represent both constraints and standalone attributes
type GridRowType = GridValidRowModel & {
  id: EntityId
  orderAttributeId?: EntityId
  vehicleAttributeId?: EntityId
  orderAttribute?: Attribute
  vehicleAttribute?: Attribute
  isCompatible?: boolean
  isSelectable: boolean
}

export const ConstraintsPage: React.FC<ConstraintsPageProps> = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridInputRowSelectionModel>()
  const [showDetails, setShowDetails] = useState(false)
  const [formType, setFormType] = useState<'add' | 'edit' | 'view'>('add')
  const [isSubmitting, setSubmitting] = useState(false)
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(false)
  const [openLinkDialog, setOpenLinkDialog] = useState(false)

  const dispatch = useAppDispatch()
  const constraints = useAppSelector(state => constraintsSelectors.selectAll(state))
  const orderAttributes = useAppSelector(selectOrderAttributes)
  const vehicleAttributes = useAppSelector(selectVehicleAttributes)
  const { setToastContent } = useToastContext()

  // Combined rows of constraints and unlinked attributes
  const rows = useMemo(() => {
    const allRows: GridRowType[] = []

    // Add all constraints
    allRows.push(...constraints.map(item => ({
      ...item,
      isSelectable: true,
    })))

    // Track all attribute IDs that are already part of constraints
    const linkedOrderAttributeIds = new Set(
      constraints.filter(c => c.orderAttributeId).map(c => c.orderAttributeId),
    )

    const linkedVehicleAttributeIds = new Set(
      constraints.filter(c => c.vehicleAttributeId).map(c => c.vehicleAttributeId),
    )

    // Add unlinked order attributes
    orderAttributes
      .filter(attr => !linkedOrderAttributeIds.has(attr.id) && !attr.hasConstraints)
      .forEach(attr => {
        allRows.push({
          id: `order_${attr.id}`, // Unique ID for the row
          orderAttributeId: attr.id,
          orderAttribute: attr,
          isSelectable: true,
        })
      })

    // Add unlinked vehicle attributes
    vehicleAttributes
      .filter(attr => !linkedVehicleAttributeIds.has(attr.id) && !attr.hasConstraints)
      .forEach(attr => {
        allRows.push({
          id: `vehicle_${attr.id}`, // Unique ID for the row
          vehicleAttributeId: attr.id,
          vehicleAttribute: attr,
          isSelectable: true,
        })
      })

    return allRows
  }, [constraints, orderAttributes, vehicleAttributes])

  // Get currently selected row data
  const selectedRow = useMemo(() => {
    if (!rowSelectionModel) return undefined

    return rows.find(row => row.id === rowSelectionModel)
  }, [rows, rowSelectionModel])

  // Fetch constraints and attributes from server
  const fetchData = async () => {
    setLoading(true)
    await Promise.all([
      dispatch(fetchConstraints()),
      dispatch(fetchAttributes()),
    ])
    setLoading(false)
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  // Define the form fields and contents based on the selected row
  const formDefinition = useMemo(() => {
    const showOrderAttribute = formType === 'add' || !!selectedRow?.orderAttributeId
    const showVehicleAttribute = formType === 'add' || !!selectedRow?.vehicleAttributeId
    const showCompatible = (formType === 'add' || (!!selectedRow?.orderAttributeId && !!selectedRow?.vehicleAttributeId))

    return getConstraintFormDefinition({
      formType,
      showOrderAttribute,
      showVehicleAttribute,
      showCompatible,
    })
  }, [formType, selectedRow])

  const handleSubmit = async (data: Record<string, unknown>) => {
    setSubmitting(true)

    // Get attribute data
    const orderAttributeData = (data.orderAttribute as Record<string, unknown>)
    const vehicleAttributeData = (data.vehicleAttribute as Record<string, unknown>)

    // Check if attributes have names
    const hasOrderAttributeName = orderAttributeData?.name && String(orderAttributeData.name).trim().length > 0
    const hasVehicleAttributeName = vehicleAttributeData?.name && String(vehicleAttributeData.name).trim().length > 0

    // Validation
    if (formType === 'add' && !hasOrderAttributeName && !hasVehicleAttributeName) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Save Attribute/Constraint Failed',
        content: 'Require Order Attribute or Vehicle Attribute name.',
      })
      setSubmitting(false)

      return
    }

    // Submit add or edit 
    try {
      const payload: Record<string, unknown> = {
        ...(selectedRow && typeof selectedRow.id === 'number' ? { id: selectedRow.id } : {}),
        ...(selectedRow?.orderAttributeId && { orderAttributeId: selectedRow.orderAttributeId }),
        ...(selectedRow?.vehicleAttributeId && { vehicleAttributeId: selectedRow.vehicleAttributeId }),
      }

      // Handle attributes based on if they have names
      if (hasOrderAttributeName) {
        payload.orderAttribute = {
          ...orderAttributeData,
          attributeType: AttributeTypeEnum.Order,
          // Keep original ID if editing
          ...(selectedRow?.orderAttributeId && { id: selectedRow.orderAttributeId }),
        }
      }

      if (hasVehicleAttributeName) {
        payload.vehicleAttribute = {
          ...vehicleAttributeData,
          attributeType: AttributeTypeEnum.Vehicle,
          // Keep original ID if editing
          ...(selectedRow?.vehicleAttributeId && { id: selectedRow.vehicleAttributeId }),
        }
      }

      // Only include compatibility if both attributes exist
      if (payload.orderAttribute && payload.vehicleAttribute) {
        payload.isCompatible = data.isCompatible
      }


      await dispatch(saveConstraint({ constraint: payload as Constraint }))
      setToastContent({
        open: true,
        severity: 'success',
        title: 'Success',
        content: 'Attribute/Constraint saved successfully',
      })
      setFormType('view')
      fetchData() // Refresh data
    } catch (error) {
      console.error('Error saving constraint:', error)
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Error',
        content: 'Failed to save attribute/constraint',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleUnlink = async () => {
    if (!selectedRow || typeof selectedRow.id !== 'number') return

    // Make sure this is a linked constraint with both order and vehicle attributes
    if (!selectedRow.orderAttributeId || !selectedRow.vehicleAttributeId) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Error',
        content: 'Cannot unlink - this is not a complete constraint',
      })

      return
    }

    try {
      const constraintId = selectedRow.id as number

      setShowDetails(false)
      setRowSelectionModel(undefined)

      await dispatch(unlinkConstraint({
        id: constraintId,
        orderAttributeId: selectedRow.orderAttributeId as number,
        vehicleAttributeId: selectedRow.vehicleAttributeId as number,
      })).then(() => {
        fetchData()
      })

      setToastContent({
        open: true,
        severity: 'success',
        title: 'Success',
        content: 'Attributes unlinked successfully',
      })
    } catch (error) {
      console.error('Error unlinking constraint:', error)
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Error',
        content: 'Failed to unlink attributes',
      })
    }
  }

  // Update form values when selectedId changes
  const resetForm = (type: 'add' | 'edit' | 'view') => {
    if (type === 'add') {
      setFormValues({
        orderAttribute: {
          name: '',
          description: '',
          attributeType: AttributeTypeEnum.Order,
        },
        vehicleAttribute: {
          name: '',
          description: '',
          attributeType: AttributeTypeEnum.Vehicle,
        },
        isCompatible: true,
      })
    } else if (selectedRow) {
      const formData: Record<string, unknown> = {
        // Always include isCompatible with a default value regardless of attributes
        isCompatible: selectedRow.isCompatible ?? true,
      }

      // Rest of your existing code to add other form fields
      if (typeof selectedRow.id === 'number') {
        formData.id = selectedRow.id
      }

      if (selectedRow.orderAttributeId) {
        formData.orderAttributeId = selectedRow.orderAttributeId
        formData.orderAttribute = selectedRow.orderAttribute || {
          id: selectedRow.orderAttributeId,
          name: '',
          description: '',
          attributeType: AttributeTypeEnum.Order,
        }
      }

      if (selectedRow.vehicleAttributeId) {
        formData.vehicleAttributeId = selectedRow.vehicleAttributeId
        formData.vehicleAttribute = selectedRow.vehicleAttribute || {
          id: selectedRow.vehicleAttributeId,
          name: '',
          description: '',
          attributeType: AttributeTypeEnum.Vehicle,
        }
      }

      setFormValues(formData)
    }
  }

  useEffect(() => {
    if (!rowSelectionModel || !['edit'].includes(formType)) return

    resetForm(formType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelectionModel, formType])

  // Define the columns for the grid
  const columns: GridColDef[] = [
    {
      field: 'orderAttribute',
      headerName: 'Order Attribute',
      width: 200,
      valueGetter: (attr: Attribute) => {
        if (!attr?.id) return ''

        return `${attr.description || ''} [${attr.name}]`
      },
    },
    {
      field: 'dummyCol',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!(params.row.orderAttributeId && params.row.vehicleAttributeId)) return null

        return (
          <div style={{ display: 'table', margin: '0 auto' }}>
            <Icon icon={params.row.isCompatible ? faLinkSimple : faLinkSimpleSlash} />
          </div>
        )
      },
    },
    {
      field: 'vehicleAttribute',
      headerName: 'Vehicle Attribute',
      width: 200,
      valueGetter: (attr: Attribute) => {
        if (!attr?.id) return ''

        return `[${attr.name}] ${attr.description || ''}`
      },
    },
    {
      field: 'isCompatible',
      headerName: 'Is Compatible?',
      width: 150,
      valueGetter: (value: boolean, row) => {
        if (row.orderAttributeId && row.vehicleAttributeId) {
          return value ? 'Yes' : 'No'
        }

        return ''
      },
    },
  ]

  const onViewRowDetail = (row: GridValidRowModel) => {
    setFormType('view')
    setShowDetails(true)
    setRowSelectionModel(row.id)
  }

  const onHandleAdd = () => {
    setFormType('add')
    resetForm('add')
    setShowDetails(true)
    setRowSelectionModel(undefined)
  }

  const onCloseDetail = () => {
    setRowSelectionModel(undefined)
    setShowDetails(false)
  }

  // Check if unlink button should be enabled
  const canUnlink = selectedRow &&
    typeof selectedRow.id === 'number' &&
    selectedRow.orderAttributeId &&
    selectedRow.vehicleAttributeId

  return (
    <StyledContainer>
      <Allotment separator={true} defaultSizes={[100, 100]}>
        <Allotment.Pane>
          <StyledLeftPane>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1">Attributes & Constraints</StyledHeader>
                <StyledDescription>
                  Configure attributes for orders and runs, and define compatibility constraints between them.
                </StyledDescription>
              </Grid>
              <Grid item sx={{ display: 'inline-flex' }}>
                <Button
                  outlined
                  startIcon={<Icon icon={faChain} />}
                  onClick={() => setOpenLinkDialog(true)}
                  sx={{ marginRight: pxToRem(8), minWidth: pxToRem(156) }}
                  tooltipTitle="Link Order and Vehicle Attributes (i.e. Create Constraint)"
                >
                  Link Attributes
                </Button>
                <StyledAddButton
                  primary
                  startIcon={<Icon icon={faAdd} />}
                  onClick={onHandleAdd}
                  tooltipTitle="Create a new attribute/constraint"
                  sx={{ minWidth: pxToRem(140) }}
                >
                  New Attribute
                </StyledAddButton>
              </Grid>
            </Grid>
            <Divider sx={{ marginTop: pxToRem(16) }} />
            <StyledGridTable
              rows={rows}
              columns={columns}
              loading={loading}
              isDragDropEnabled={false}
              rowSelectionModel={rowSelectionModel}
              onRowClick={onViewRowDetail}
              slots={{
                toolbar: CustomGridToolbar,
              }}
            />
          </StyledLeftPane>
        </Allotment.Pane>
        <Allotment.Pane visible={showDetails} minSize={360}>
          <StyledRightPane $visible={showDetails}>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1" sx={{ paddingLeft: 0 }}>
                  {formType === 'add' && 'Add a new attribute/constraint'}
                  {formType === 'edit' && 'Edit attribute/constraint details'}
                  {formType === 'view' && (
                    <>
                      {formValues?.orderAttribute && (formValues.orderAttribute as Attribute)?.name || ''}
                      {formValues?.orderAttribute && formValues?.vehicleAttribute && ' - '}
                      {formValues?.vehicleAttribute && (formValues.vehicleAttribute as Attribute)?.name || ''}
                    </>
                  )}
                </StyledHeader>
              </Grid>
              <StyledButtonsContainer item>
                {formType !== 'add' && (
                  <>
                    <Button
                      outlined
                      startIcon={<Icon icon={faChainBroken} />}
                      onClick={handleUnlink}
                      disabled={isSubmitting || !canUnlink}
                      tooltipTitle={canUnlink
                        ? 'Unlink Order and Vehicle Attributes (i.e. Remove Constraint)'
                        : 'Cannot unlink - missing Order or Vehicle Attribute'
                      }
                    >
                      Unlink
                    </Button>
                    <ToggleButton
                      value="checked"
                      primary
                      text
                      selected={formType === 'edit'}
                      onChange={() => {
                        resetForm(formType === 'edit' ? 'view' : 'edit')
                        setFormType((prev) => prev === 'edit' ? 'view' : 'edit')
                      }}
                      disabled={!rowSelectionModel}
                      sx={{ minWidth: pxToRem(100) }}
                    >
                      <Icon icon={faEdit} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                      {formType === 'edit' ? 'Cancel Edit' : 'Edit'}
                    </ToggleButton>
                  </>
                )}
                <IconButton size="small" onClick={() => onCloseDetail()}>
                  <Icon icon={faClose} sx={{ fontSize: pxToRem(18) }} />
                </IconButton>
              </StyledButtonsContainer>
            </Grid>
            <DynamicForm
              {...formDefinition}
              initialValues={formValues}
              onSubmit={handleSubmit}
              isReadOnly={isSubmitting || !showDetails || formType === 'view'}
              onCancel={() => {
                resetForm(formType)

                if (formType === 'add') {
                  setShowDetails(false)
                }

                setFormType('view')
              }}
              slotProps={{
                submitButton: {
                  disabled: isSubmitting,
                },
              }}
            />
          </StyledRightPane>
        </Allotment.Pane>
      </Allotment>

      <LinkConstraintsDialog
        open={openLinkDialog}
        onClose={(refresh) => {
          setOpenLinkDialog(false)

          if (refresh) {
            fetchData()
          }
        }}
        orderAttributes={orderAttributes.filter(attr => !attr.hasConstraints)}
        vehicleAttributes={vehicleAttributes.filter(attr => !attr.hasConstraints)}
      />
    </StyledContainer>
  )
}

const StyledGridTable = styled(GridTable)`
  height: calc(100% - ${pxToRem(86)});
`